import React from 'react'

import { Pagination, PaginationEventType } from '@ui/v2/Pagination/Pagination'

import { PageMetaData, PageQuery } from '@utils/ApiClient'
import { useWindowResize } from '@utils/domUtils'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { BankAccount, isBankAccountApproved } from '@model/BankAccount'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentClientAreaDto } from '@model/DocumentClientAreaDto'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'

import { UploadedDocumentsCards as UploadedDocumentsCardsView } from './UploadedDocumentCards'
import { UploadedDocumentsTable as UploadedDocumentsTableView } from './UploadedDocumentsTable'

interface UploadedDocumentsProps {
  documents: DocumentClientAreaDto[]
  bankAccounts: BankAccount[]
  isLoading: boolean
  pageQuery?: PageQuery
  meta?: PageMetaData
  setPageQuery: ((pageQuery?: PageQuery | undefined) => void) | undefined
  forceRefresh(): void
  setIsPaginationEntrySelected?: (isPaginationEntrySelected: boolean) => void
}

export const isUploadLocked = (
  doc: DocumentClientAreaDto,
  account: AccountDetailedDto | undefined
) => {
  return (
    (doc.category.id === DocumentCategoryType.Personal && account?.isPersonalInfoLocked) ||
    (doc.category.id === DocumentCategoryType.Address && account?.isAddressInfoLocked) ||
    (doc.category.id === DocumentCategoryType.Additional && account?.isAdditionalInfoLocked)
  )
}

const useShowUploadedDocuments = (props: UploadedDocumentsProps) => {
  const { documents, meta } = props
  return {
    isPage: !!documents.length && !!meta,
  }
}

export const UploadedDocuments: React.FC<UploadedDocumentsProps> = (props) => {
  const {
    documents,
    forceRefresh,
    meta,
    bankAccounts,
    isLoading,
    setPageQuery,
    setIsPaginationEntrySelected,
  } = props

  const isMobile = useWindowResize()
  const { isPage } = useShowUploadedDocuments(props)

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      {!isMobile && (
        <UploadedDocumentsTableView
          documents={documents}
          forceRefresh={forceRefresh}
          readOnlyDocumentIds={filterBankAccountsDocumentIds(bankAccounts)}
        />
      )}
      {isMobile && (
        <UploadedDocumentsCardsView
          documents={documents}
          forceRefresh={forceRefresh}
          readOnlyDocumentIds={filterBankAccountsDocumentIds(bankAccounts)}
        />
      )}
      {isPage && setIsPaginationEntrySelected && meta && (
        <Pagination
          scrollToHeaderId={ScrollToIds.UploadedDocumentsHeader}
          pageData={meta}
          isLoading={isLoading}
          onPageChanged={(pageIndex, pageSize, eventType) => {
            if (eventType === PaginationEventType.ENTRIES_CHANGED) {
              setIsPaginationEntrySelected(true)
            }
            setPageQuery?.({
              pageIndex,
              pageSize,
            })
          }}
        />
      )}
    </Loading>
  )
}

export const UploadedDocumentsCards = (props: UploadedDocumentsProps) => {
  const { documents, isLoading, bankAccounts, forceRefresh } = props

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      <UploadedDocumentsCardsView
        documents={documents}
        forceRefresh={forceRefresh}
        readOnlyDocumentIds={filterBankAccountsDocumentIds(bankAccounts)}
      />
    </Loading>
  )
}

const filterBankAccountsDocumentIds = (bankAccounts: BankAccount[]): string[] => {
  return bankAccounts?.flatMap((item) =>
    isBankAccountApproved(item) && item.document ? [item.document.id] : []
  )
}
